// Api Call functions

import axios from 'axios';
import configs from '../config/app';
import urlConfigs, { urlPillars } from './urlConfigs';
import { BaseUrl } from './baseUrl';
import { setSessionExpired } from '../features/authentication/authSlice';
import { store } from '../store/store';
import { cacheGet } from '../util/cache';

const BaseApiUrl = configs.obeliskApi;
const axiosInstance = axios.create({
  baseURL: BaseApiUrl,
});

axiosInstance.interceptors.response.use(
  async (response) => response,
  async (error) => {
    if (error.response) {
      if (
        error?.response?.status === 401 &&
        error?.response?.data?.error === 'Token is expired, login again.'
      ) {
        store.dispatch(setSessionExpired(true));
      }
    }
    return Promise.reject(error);
  }
);

// Post Call Function
export const postCall = async (postData = {}) => {
  let url = BaseApiUrl;
  let bodyData = {};
  let headers = {
    'Content-Type': 'application/json',
  };

  if (postData.accessToken && postData.accessToken === 'false') {
    delete headers['Authorization'];
  } else {
    const { jwtToken } = cacheGet('apiToken', true, { jwtToken: null });
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  if (postData.bodyData) {
    bodyData = postData.bodyData;
  }

  if (postData.header) {
    headers = { ...headers, ...postData.header };
  }

  if (postData.fullUrl) {
    url = postData.fullUrl;
  }

  if (postData.url) {
    url += urlConfigs[postData.url];
  }

  if (postData.callBackData) {
    postData.bodyData['callBackData'] = postData.callBackData;
  }

  return await axiosInstance
    .post(url, bodyData, { headers })
    .then((response) => {
      postData.successCallBack && postData.successCallBack(response, postData.bodyData);
      return response;
    })
    .catch((error) => {
      postData.errorCallBack && postData.errorCallBack(error);
      return error;
    });
};

// Put Call Function
export const putCall = async (putData = {}) => {
  let url = BaseApiUrl;
  let bodyData = {};
  let headers = {
    'Content-Type': 'application/json',
  };

  if (putData.accessToken && putData.accessToken === 'false') {
    delete headers['Authorization'];
  } else {
    const { jwtToken } = cacheGet('apiToken', true, { jwtToken: null });
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  if (putData.bodyData) {
    bodyData = putData.bodyData;
  }

  if (putData.header) {
    headers = { ...headers, ...putData.header };
  }

  if (putData.fullUrl) {
    url = putData.fullUrl;
  }

  if (putData.url) {
    url += urlConfigs[putData.url];
  }

  if (putData.baseUrl) {
    url = BaseUrl + urlPillars[putData.baseUrl];
  }

  return axiosInstance
    .put(url, bodyData, { headers })
    .then((response) => {
      putData.successCallBack && putData.successCallBack(response);
      return response;
    })
    .catch((error) => {
      putData.errorCallBack && putData.errorCallBack(error);
      return error;
    });
};

// Get Call Function
export const getCall = async (getData = {}) => {
  let url = BaseApiUrl;
  let headers = {
    'Content-Type': 'application/json',
  };
  const removeAuth = getData.accessToken && getData.accessToken === 'false';
  if (removeAuth) {
    delete headers['Authorization'];
  }

  if (getData.fullUrl) {
    url = getData.fullUrl;
  }

  if (getData.url) {
    url += urlConfigs[getData.url];
  }

  if (getData.baseUrl) {
    url = BaseUrl + urlPillars[getData.baseUrl];
  }

  if (getData.headers) {
    headers = { ...headers, ...getData.headers };
  }

  const { jwtToken } = cacheGet('apiToken', true, { jwtToken: null });
  if (!(getData.headers && getData.headers['Authorization']) && jwtToken && !removeAuth) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  if (getData.urlParams && Object.entries(getData.urlParams).length) {
    for (const [key, value] of Object.entries(getData.urlParams)) {
      if (key) {
        if (url.indexOf('?') >= 0) {
          url += `&${key}=${value}`;
        } else {
          url += `?${key}=${value}`;
        }
      }
    }
  }

  return await axiosInstance
    .get(url, { headers })
    .then((response) => {
      getData.successCallBack && getData.successCallBack(response, getData);
      return response;
    })
    .catch((error) => {
      getData.errorCallBack && getData.errorCallBack(error);
      return error;
    });
};

// Delete Call Function
export const deleteCall = async (deleteData = {}) => {
  let url = BaseApiUrl;
  let bodyData = {};
  let headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };

  if (deleteData.accessToken && deleteData.accessToken === 'false') {
    delete headers['Authorization'];
  } else {
    const { jwtToken } = cacheGet('apiToken', true, { jwtToken: null });
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  if (deleteData.bodyData) {
    bodyData = { ...bodyData, ...deleteData.bodyData };
  }

  if (deleteData.header) {
    headers = { ...headers, ...deleteData.header };
  }

  if (deleteData.fullUrl) {
    url = deleteData.fullUrl;
  }

  if (deleteData.url) {
    url += urlConfigs[deleteData.url];
  }

  if (deleteData.urlParams && Object.entries(deleteData.urlParams).length) {
    for (const [key, value] of Object.entries(deleteData.urlParams)) {
      if (key) {
        if (url.indexOf('?') >= 0) {
          url += `&${key}=${value}`;
        } else {
          url += `?${key}=${value}`;
        }
      }
    }
  }

  return await axiosInstance
    .delete(url, {
      headers,
      data: bodyData,
    })
    .then((response) => {
      deleteData.successCallBack && deleteData.successCallBack(response, deleteData);
      return response;
    })
    .catch((error) => {
      deleteData.errorCallBack && deleteData.errorCallBack(error, deleteData);
      return error;
    });
};
